'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AirportPicker from './AirportPicker';
import DatePicker from './DatePicker';
import PassengerSelector from './PassengerSelector';
import CabinClassSelector from './CabinClassSelector';

export type TripType = 'round_trip' | 'one_way' | 'multi_city';
export type CabinClass = 'economy' | 'premium_economy' | 'business' | 'first';

export interface PassengerCounts {
  adults: number;
  children: number;
  infants: number;
}

export interface SearchParams {
  tripType: TripType;
  origin: string;
  originCode: string;
  destination: string;
  destinationCode: string;
  departureDate: string;
  returnDate: string;
  passengers: PassengerCounts;
  cabinClass: CabinClass;
}

interface MultiCityLeg {
  id: string;
  origin: string;
  originCode: string;
  destination: string;
  destinationCode: string;
  date: string;
}

export default function FlightSearchHero() {
  const router = useRouter();
  const [tripType, setTripType] = useState<TripType>('round_trip');
  const [origin, setOrigin] = useState('New York (JFK)');
  const [originCode, setOriginCode] = useState('JFK');
  const [destination, setDestination] = useState('Madrid (MAD)');
  const [destinationCode, setDestinationCode] = useState('MAD');
  const [departureDate, setDepartureDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [returnDate, setReturnDate] = useState(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek.toISOString().split('T')[0];
  });
  const [passengers, setPassengers] = useState<PassengerCounts>({ adults: 1, children: 0, infants: 0 });
  const [cabinClass, setCabinClass] = useState<CabinClass>('economy');
  const [searching, setSearching] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [multiCityLegs, setMultiCityLegs] = useState<MultiCityLeg[]>([
    { id: 'leg-1', origin: 'New York (JFK)', originCode: 'JFK', destination: 'London (LHR)', destinationCode: 'LHR', date: '2026-09-05' },
    { id: 'leg-2', origin: 'London (LHR)', originCode: 'LHR', destination: 'Paris (CDG)', destinationCode: 'CDG', date: '2026-09-10' },
  ]);

  const tripTypes: { value: TripType; label: string }[] = [
    { value: 'round_trip', label: 'Round Trip' },
    { value: 'one_way', label: 'One Way' },
    { value: 'multi_city', label: 'Multi-City' },
  ];

  const handleSwap = () => {
    setOrigin(destination);
    setOriginCode(destinationCode);
    setDestination(origin);
    setDestinationCode(originCode);
  };

  const addLeg = () => {
    if (multiCityLegs.length >= 5) return;
    const lastLeg = multiCityLegs[multiCityLegs.length - 1];
    setMultiCityLegs([
      ...multiCityLegs,
      {
        id: `leg-${multiCityLegs.length + 1}`,
        origin: lastLeg.destination,
        originCode: lastLeg.destinationCode,
        destination: '',
        destinationCode: '',
        date: lastLeg.date,
      },
    ]);
  };

  const removeLeg = (id: string) => {
    if (multiCityLegs.length <= 2) return;
    setMultiCityLegs(multiCityLegs.filter((l) => l.id !== id));
  };

  const updateLeg = (id: string, field: Partial<MultiCityLeg>) => {
    setMultiCityLegs(multiCityLegs.map((l) => (l.id === id ? { ...l, ...field } : l)));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (tripType === 'multi_city') {
      multiCityLegs.forEach((leg, i) => {
        if (!leg.originCode) newErrors[`leg-${i}-origin`] = 'Select departure airport';
        if (!leg.destinationCode) newErrors[`leg-${i}-dest`] = 'Select destination airport';
        if (!leg.date) newErrors[`leg-${i}-date`] = 'Select date';
      });
    } else {
      if (!originCode) newErrors.origin = 'Please select a departure airport.';
      if (!destinationCode) newErrors.destination = 'Please select a destination airport.';
      if (originCode === destinationCode) newErrors.destination = 'Origin and destination must be different.';
      if (!departureDate) newErrors.departureDate = 'Please select a departure date.';
      if (tripType === 'round_trip' && !returnDate) newErrors.returnDate = 'Please select a return date.';
      if (tripType === 'round_trip' && returnDate && departureDate && returnDate < departureDate) {
        newErrors.returnDate = 'Return date must be after departure date.';
      }
    }
    if (passengers.adults < 1) newErrors.passengers = 'At least 1 adult passenger required.';
    return newErrors;
  };

  const handleSearch = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSearching(true);

    if (tripType === 'multi_city') {
      const firstLeg = multiCityLegs[0];
      const query = new URLSearchParams({
        origin: firstLeg.origin,
        originCode: firstLeg.originCode,
        destination: multiCityLegs[multiCityLegs.length - 1].destination,
        destinationCode: multiCityLegs[multiCityLegs.length - 1].destinationCode,
        departureDate: firstLeg.date,
        returnDate: '',
        tripType: 'multi_city',
        adults: String(passengers.adults),
        children: String(passengers.children),
        infants: String(passengers.infants),
        cabinClass,
        legs: JSON.stringify(multiCityLegs),
      });
      setSearching(false);
      router.push(`/flight-results?${query.toString()}`);
      return;
    }

    const params: SearchParams = {
      tripType,
      origin,
      originCode,
      destination,
      destinationCode,
      departureDate,
      returnDate: tripType === 'round_trip' ? returnDate : '',
      passengers,
      cabinClass,
    };

    const query = new URLSearchParams({
      origin: params.origin,
      originCode: params.originCode,
      destination: params.destination,
      destinationCode: params.destinationCode,
      departureDate: params.departureDate,
      returnDate: params.returnDate,
      tripType: params.tripType,
      adults: String(params.passengers.adults),
      children: String(params.passengers.children),
      infants: String(params.passengers.infants),
      cabinClass: params.cabinClass,
    });

    setSearching(false);
    router.push(`/flight-results?${query.toString()}`);
  };

  const totalPassengers = passengers.adults + passengers.children + passengers.infants;
  const cabinLabels: Record<CabinClass, string> = {
    economy: 'Economy',
    premium_economy: 'Premium Economy',
    business: 'Business',
    first: 'First Class',
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{
        backgroundImage: "url('/assets/images/178822170264173251__1_-1788221894214.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/40" />
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 right-20 w-64 h-64 rounded-full bg-white opacity-5 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-48 rounded-full bg-white opacity-5 blur-3xl" />
        <svg
          className="absolute top-8 right-8 opacity-10 hidden lg:block"
          width="200"
          height="60"
          viewBox="0 0 200 60"
          fill="none"
        >
          <path d="M10 50 Q50 10 100 30 Q150 50 190 15" stroke="white" strokeWidth="2" fill="none" strokeDasharray="4 4" />
        </svg>
      </div>

      <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 pt-16 pb-24">
        <div className="text-center mb-10">
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight mb-4">
            Find Your Next Flight
          </h1>
          <p className="text-sky-100 text-lg lg:text-xl font-medium">
            Search and book flights to anywhere in the world. Pay with crypto and save 10%. No account needed, no hidden fees.
          </p>
        </div>

        {/* Search Card */}
        <div className="bg-card rounded-2xl card-shadow max-w-5xl xl:max-w-6xl mx-auto overflow-visible">
          {/* Trip Type Tabs */}
          <div className="flex border-b border-border px-6 pt-4">
            {tripTypes.map((t) => (
              <button
                key={`trip-type-${t.value}`}
                onClick={() => setTripType(t.value)}
                className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg mr-1 transition-all duration-150 ${
                  tripType === t.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Passenger + Cabin Row */}
            <div className="flex flex-wrap gap-3 mb-5">
              <PassengerSelector passengers={passengers} onChange={setPassengers} />
              <CabinClassSelector value={cabinClass} onChange={setCabinClass} />
            </div>

            {/* Multi-City Legs */}
            {tripType === 'multi_city' ? (
              <div className="space-y-3">
                {multiCityLegs.map((leg, i) => (
                  <div key={leg.id} className="relative border border-border rounded-xl p-4 bg-muted/20">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                        Flight {i + 1}
                      </span>
                      {multiCityLegs.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeLeg(leg.id)}
                          className="text-xs text-red-500 hover:text-red-700 font-semibold transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
                      <div className="lg:col-span-4">
                        <AirportPicker
                          label="From"
                          placeholder="City or airport"
                          value={leg.origin}
                          onChange={(label, code) => {
                            updateLeg(leg.id, { origin: label, originCode: code });
                            setErrors((e) => ({ ...e, [`leg-${i}-origin`]: '' }));
                          }}
                          error={errors[`leg-${i}-origin`]}
                        />
                      </div>
                      <div className="lg:col-span-1 flex items-center justify-center pt-7">
                        <button
                          type="button"
                          onClick={() => updateLeg(leg.id, { origin: leg.destination, originCode: leg.destinationCode, destination: leg.origin, destinationCode: leg.originCode })}
                          className="w-9 h-9 rounded-full border-2 border-border bg-card hover:border-primary hover:bg-secondary flex items-center justify-center transition-all duration-200 active:scale-95 group"
                        >
                          <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                        </button>
                      </div>
                      <div className="lg:col-span-4">
                        <AirportPicker
                          label="To"
                          placeholder="City or airport"
                          value={leg.destination}
                          onChange={(label, code) => {
                            updateLeg(leg.id, { destination: label, destinationCode: code });
                            setErrors((e) => ({ ...e, [`leg-${i}-dest`]: '' }));
                          }}
                          error={errors[`leg-${i}-dest`]}
                        />
                      </div>
                      <div className="lg:col-span-3">
                        <DatePicker
                          label="Date"
                          value={leg.date}
                          onChange={(v) => {
                            updateLeg(leg.id, { date: v });
                            setErrors((e) => ({ ...e, [`leg-${i}-date`]: '' }));
                          }}
                          error={errors[`leg-${i}-date`]}
                          minDate="2026-08-29"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {multiCityLegs.length < 5 && (
                  <button
                    type="button"
                    onClick={addLeg}
                    className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-sky-600 transition-colors px-2 py-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add another flight
                  </button>
                )}
              </div>
            ) : (
              /* Airport + Date Row for round trip / one way */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
                {/* Origin */}
                <div className="lg:col-span-4 relative">
                  <AirportPicker
                    label="From"
                    placeholder="City or airport"
                    value={origin}
                    onChange={(label, code) => {
                      setOrigin(label);
                      setOriginCode(code);
                      setErrors((e) => ({ ...e, origin: '' }));
                    }}
                    error={errors.origin}
                  />
                </div>

                {/* Swap Button */}
                <div className="lg:col-span-1 flex items-center justify-center pt-7">
                  <button
                    onClick={handleSwap}
                    className="w-9 h-9 rounded-full border-2 border-border bg-card hover:border-primary hover:bg-secondary flex items-center justify-center transition-all duration-200 active:scale-95 group"
                    title="Swap origin and destination"
                  >
                    <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </button>
                </div>

                {/* Destination */}
                <div className="lg:col-span-4">
                  <AirportPicker
                    label="To"
                    placeholder="City or airport"
                    value={destination}
                    onChange={(label, code) => {
                      setDestination(label);
                      setDestinationCode(code);
                      setErrors((e) => ({ ...e, destination: '' }));
                    }}
                    error={errors.destination}
                  />
                </div>

                {/* Departure Date */}
                <div className="lg:col-span-2">
                  <DatePicker
                    label="Departure"
                    value={departureDate}
                    onChange={(v) => {
                      setDepartureDate(v);
                      setErrors((e) => ({ ...e, departureDate: '' }));
                    }}
                    error={errors.departureDate}
                    minDate="2026-08-29"
                  />
                </div>

                {/* Return Date */}
                {tripType === 'round_trip' && (
                  <div className="lg:col-span-2">
                    <DatePicker
                      label="Return"
                      value={returnDate}
                      onChange={(v) => {
                        setReturnDate(v);
                        setErrors((e) => ({ ...e, returnDate: '' }));
                      }}
                      error={errors.returnDate}
                      minDate={departureDate || '2026-08-29'}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Search Button */}
            <div className="mt-5 flex justify-center lg:justify-end">
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-10 py-3.5 bg-accent text-accent-foreground font-bold text-base rounded-xl hover:bg-orange-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2.5 min-w-[180px] justify-center card-shadow"
              >
                {searching ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    Search Flights
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}